import { useMemo, useRef, useEffect, useState } from 'react';

// Frequency-based color palette — high frequency = bold/dark, low = light/soft
const FREQ_COLORS = [
  // Index 0 = highest frequency → darkest/boldest
  '#1e3a8a', // blue-900
  '#1d4ed8', // blue-700
  '#2563eb', // blue-600
  '#3b82f6', // blue-500
  '#60a5fa', // blue-400
  '#93c5fd', // blue-300
  '#bfdbfe', // blue-200
];

const MIN_FONT = 14;
const MAX_FONT = 80;
const MAX_WORDS = 40; // Show top 40 words for readability

function seededRandom(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return ((hash % 200) - 100) / 1000;
}

/**
 * Sort words for spiral layout: highest frequency in the center,
 * then alternating left/right with decreasing frequency.
 */
function spiralSort(words) {
  if (words.length <= 1) return words;
  const sorted = [...words].sort((a, b) => b.count - a.count);
  const result = [];
  let left = 0;
  let right = sorted.length - 1;
  let insertLeft = true;

  // Place biggest word first (center), then alternate sides
  result.push(sorted[0]);
  for (let i = 1; i < sorted.length; i++) {
    if (insertLeft) {
      result.unshift(sorted[i]);
    } else {
      result.push(sorted[i]);
    }
    insertLeft = !insertLeft;
  }
  return result;
}

export default function WordCloudDisplay({ slide, responses, participantCount }) {
  const [prevCount, setPrevCount] = useState(0);
  const containerRef = useRef(null);

  const { words, maxCount, totalResponses } = useMemo(() => {
    const freq = {};
    for (const r of responses) {
      if (!r.value) continue;
      const tokens = String(r.value).trim().split(/\s+/);
      for (const raw of tokens) {
        const word = raw.toLowerCase().replace(/[^a-zA-Z0-9æøåÆØÅ\u00C0-\u024F-]/g, '');
        if (!word || word.length < 2) continue; // Skip single chars
        freq[word] = (freq[word] || 0) + 1;
      }
    }

    // Sort by frequency descending, take top N
    const entries = Object.entries(freq)
      .map(([text, count]) => ({ text, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, MAX_WORDS);

    const max = entries.reduce((m, w) => Math.max(m, w.count), 1);

    // Spiral sort for center-focused layout
    const spiraled = spiralSort(entries);

    return { words: spiraled, maxCount: max, totalResponses: responses.length };
  }, [responses]);

  // Track response count for pulse animation
  useEffect(() => {
    if (totalResponses > prevCount && prevCount > 0) {
      // New response came in — trigger subtle pulse on counter
      const el = containerRef.current?.querySelector('.response-counter');
      if (el) {
        el.classList.remove('counter-pulse');
        void el.offsetWidth; // Force reflow
        el.classList.add('counter-pulse');
      }
    }
    setPrevCount(totalResponses);
  }, [totalResponses, prevCount]);

  if (totalResponses === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 w-full">
        <span className="material-symbols-outlined text-6xl text-blue-400/30 mb-2">cloud</span>
        <p className="text-blue-300/60 text-2xl font-medium animate-pulse">Ventar på svar frå deltakarane...</p>
        <p className="text-blue-400/40 text-sm">Del PIN-koden for å kome i gang</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex flex-col items-center w-full gap-4">
      {/* Live response counter */}
      <div className="response-counter flex items-center gap-2 bg-blue-500/20 backdrop-blur-sm rounded-full px-4 py-1.5">
        <span className="material-symbols-outlined text-blue-400 text-[18px]">group</span>
        <span className="text-blue-300 font-bold text-sm">{totalResponses} svar</span>
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
        </span>
      </div>

      {/* Word cloud — spiral layout with frequency-based sizing and coloring */}
      <div
        className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 max-w-5xl w-full min-h-[250px] p-6"
        role="img"
        aria-label="Ordsky"
      >
        {words.map((word, i) => {
          const ratio = maxCount > 1 ? (word.count - 1) / (maxCount - 1) : 0;
          const fontSize = MIN_FONT + ratio * (MAX_FONT - MIN_FONT);

          // Color by frequency: high count = dark/bold, low count = light/soft
          const colorIndex = Math.min(
            Math.floor((1 - ratio) * (FREQ_COLORS.length - 1)),
            FREQ_COLORS.length - 1
          );
          const color = FREQ_COLORS[colorIndex];

          // Subtle rotation for visual interest
          const rotation = seededRandom(word.text) * 15;

          // Font weight scales with frequency
          const fontWeight = ratio > 0.6 ? 900 : ratio > 0.3 ? 700 : 500;

          return (
            <span
              key={word.text}
              className="inline-block px-1.5 py-0.5 cursor-default hover:scale-110 transition-all duration-300 ease-out"
              style={{
                fontSize: `${fontSize}px`,
                color,
                fontWeight,
                transform: `rotate(${rotation}deg)`,
                textShadow: ratio > 0.5 ? '0 0 20px rgba(59,130,246,0.3)' : 'none',
                animation: `wordAppear 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 0.04}s both`,
              }}
              title={`${word.text}: ${word.count} ${word.count === 1 ? 'gong' : 'gonger'}`}
            >
              {word.text}
            </span>
          );
        })}
      </div>

      {/* Unique words count */}
      <p className="text-blue-400/40 text-xs">
        {words.length} unike ord
      </p>

      <style>{`
        @keyframes wordAppear {
          from {
            opacity: 0;
            transform: scale(0.3) rotate(0deg);
            filter: blur(4px);
          }
          to {
            opacity: 1;
            filter: blur(0);
          }
        }

        .counter-pulse {
          animation: pulseCounter 0.5s ease-out;
        }

        @keyframes pulseCounter {
          0% { transform: scale(1); }
          50% { transform: scale(1.15); background-color: rgba(59,130,246,0.35); }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
